import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import {
  AlertCircle,
  AlertTriangle,
  FileText,
  Info,
} from '@lifeomic/chromicons';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SYSTEM_CARE_PLAN_COMPOSITE_REPORT_DEFINITION_DURABLE_ID } from '../../../../util/fhir';
import EmptyPageState from '../../../EmptyPageState';
import { ImpressionEditor } from '../AITools/ImpressionEditor';
import { CRDSelect } from '../ExecutiveSummary/GenerateMenuModal/CRDSelect';
import { useCrdTemplateSectionAvailability } from '../ExecutiveSummary/useCrdTemplateSectionAvailability';
import { useRecursiveCRDFetch } from '../ExecutiveSummary/useRecursiveCRDFetch';
import { SectionRegenerateButton } from './SectionRegenerateButton';
import { useCompositeReportCarePlans } from './useCompositeReportCarePlans';
import { useCompositeReportGeneration } from './useCompositeReportGeneration';
import { useCompositeReportSections } from './useCompositeReportSections';
import { useIsCompositeReportEnabled } from './useIsCompositeReportEnabled';

const messages = defineMessages({
  generate: {
    id: 'CompositeReportModule.generate',
    defaultMessage: 'Generate',
  },
  noCrdsAvailable: {
    id: 'CompositeReportModule.noCrdsAvailable',
    defaultMessage:
      'No composite report templates are available for this account.',
  },
  generating: {
    id: 'CompositeReportModule.generating',
    defaultMessage: 'Generating composite report… this may take a few minutes.',
  },
  alreadyGenerating: {
    id: 'CompositeReportModule.alreadyGenerating',
    defaultMessage:
      'A composite report is already being generated for this patient. Showing progress below.',
  },
  noReportYet: {
    id: 'CompositeReportModule.noReportYet',
    defaultMessage:
      'No composite report has been generated yet for this patient. Select a template above and click Generate.',
  },
  sectionsLoadError: {
    id: 'CompositeReportModule.sectionsLoadError',
    defaultMessage:
      'Something went wrong loading the composite report sections.',
  },
  sectionTruncated: {
    id: 'CompositeReportModule.sectionTruncated',
    defaultMessage:
      'Content may be incomplete — this section reached the output limit.',
  },
});

export type CompositeReportModuleProps = {
  patient: fhir.Patient & { id: string };
};

// §1.4: runtime-safe extraction instead of an unchecked `as string` cast —
// `valueString` is typed as `string | undefined` by the FHIR types, but that
// is a compile-time-only guarantee. A cast provides no protection against a
// malformed/drifted real-world extension (e.g. a non-string value written by
// a future bug), so this narrows with `typeof` and falls back to `undefined`
// rather than silently treating garbage data as a valid CRD durable id.
export const getCrdDurableIdFromCarePlan = (
  carePlan?: fhir3.CarePlan,
): string | undefined => {
  const value = carePlan?.extension?.find(
    ext => ext.url === SYSTEM_CARE_PLAN_COMPOSITE_REPORT_DEFINITION_DURABLE_ID,
  )?.valueString;
  return typeof value === 'string' ? value : undefined;
};

/**
 * CR-9/CR-10: Top-level Composite Report module. Lets a clinician pick a
 * Composite Report Definition (CRD), generate a report for the current
 * subject, and view/regenerate the resulting sections in CRD order.
 *
 * All data-fetching hooks live in `CompositeReportModuleContent`, which is
 * only mounted once `useIsCompositeReportEnabled()` is true — this guarantees
 * the module makes zero network calls while the `genericCompositeReport`
 * feature flag is off.
 */
const CompositeReportModuleContent: React.FC<CompositeReportModuleProps> = ({
  patient,
}) => {
  const { formatMessage } = useIntl();

  const { compositeReportDefinitions, isLoading: isLoadingCrds } =
    useRecursiveCRDFetch();
  const { hasTemplateSections } = useCrdTemplateSectionAvailability(
    compositeReportDefinitions,
  );

  const {
    latestCarePlan,
    loading: isLoadingCarePlans,
    refetch: refetchCarePlans,
  } = useCompositeReportCarePlans(patient.id);

  const {
    sections,
    loading: isLoadingSections,
    error: sectionsError,
    refetch: refetchSections,
  } = useCompositeReportSections(latestCarePlan);

  const [crdDurableId, setCrdDurableId] = useState<string | undefined>(
    undefined,
  );
  const [conflictNotice, setConflictNotice] = useState(false);

  // Default the CRD picker to whichever CRD produced the existing report, or
  // (once CRDs have loaded) the first available CRD — same convention as the
  // Executive Summary generate form.
  //
  // We wait for CarePlans to finish loading before falling back to the first
  // available CRD: otherwise, if CRDs resolve before CarePlans, we'd default
  // to the first CRD and then never reconsider once the CarePlan (with its
  // own CRD) arrives, since this effect bails out as soon as `crdDurableId`
  // is set.
  useEffect(() => {
    if (crdDurableId) return;
    if (isLoadingCarePlans) return;
    const fromCarePlan = getCrdDurableIdFromCarePlan(latestCarePlan);
    if (fromCarePlan) {
      setCrdDurableId(fromCarePlan);
      return;
    }
    if (isLoadingCrds) return;
    if (compositeReportDefinitions[0]) {
      setCrdDurableId(compositeReportDefinitions[0].durableId);
    }
  }, [
    crdDurableId,
    latestCarePlan,
    isLoadingCarePlans,
    isLoadingCrds,
    compositeReportDefinitions,
  ]);

  // Sections are keyed by ClinicalImpression id, which stays the same across
  // a regenerate — so refetching CarePlans alone isn't enough to pick up the
  // newly-generated content. Refetch both after any successful generation.
  const handleGenerationSuccess = useCallback(() => {
    refetchCarePlans();
    refetchSections();
  }, [refetchCarePlans, refetchSections]);

  const { invoke, isInvoking, isPolling, error } = useCompositeReportGeneration(
    {
      subjectId: patient.id,
      onGenerationSuccess: handleGenerationSuccess,
    },
  );

  const isBusy = isInvoking || isPolling;

  const handleGenerate = async () => {
    if (!crdDurableId || isBusy) return;
    setConflictNotice(false);
    try {
      const result = await invoke(crdDurableId);
      if (result === 'conflict') {
        setConflictNotice(true);
      }
    } catch {
      // Non-conflict errors are surfaced via the `error` returned by the hook.
    }
  };

  const hasNoCrds = compositeReportDefinitions.length === 0 && !isLoadingCrds;

  // Sections always reflect `latestCarePlan`, not whatever the picker is
  // currently set to (the clinician may change the picker without hitting
  // Generate). Regenerate must target the CRD that actually produced the
  // displayed report, falling back to the picker only when there's no report
  // yet (e.g. the CRD picker's own initial default).
  const sectionsCrdId =
    getCrdDurableIdFromCarePlan(latestCarePlan) ?? crdDurableId ?? '';

  return (
    // `direction="column"` + `overflow: hidden` lets the scrollable region
    // below own its own `overflow-y: auto` instead of relying on the
    // SubjectViewer/MemberDetail ancestor (which itself is a fixed-height,
    // `overflow: hidden` flex column — see MemberDetail's `mainContent`
    // style) to somehow make this module scroll on its behalf.
    <Box
      direction="column"
      gap={2}
      fullWidth
      style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
    >
      <Box gap={2} align="flex-end" fullWidth style={{ flexShrink: 0 }}>
        {hasNoCrds ? (
          <Text size="body">{formatMessage(messages.noCrdsAvailable)}</Text>
        ) : (
          <Box fullWidth>
            <CRDSelect
              crdDurableId={crdDurableId}
              setCrdDurableId={id => {
                setCrdDurableId(id);
                setConflictNotice(false);
              }}
              compositeReportDefinitions={compositeReportDefinitions}
              hasTemplateSections={hasTemplateSections}
            />
          </Box>
        )}
        {/*
          `flexShrink: 0` keeps this Button at its label's natural width.
          Without it, the sibling `Box fullWidth` above claims all the row's
          space, squeezing the Button below its content width — and Chroma's
          Button applies `overflow: hidden; text-overflow: ellipsis`
          internally, so a squeezed button visually truncates its label
          instead of wrapping (same fix as the `flexShrink: 0` wrapper used
          for the banner in ExecutiveSummary's mainContent).
        */}
        <Button
          onClick={handleGenerate}
          disabled={!crdDurableId || isBusy}
          style={{ flexShrink: 0 }}
        >
          {formatMessage(messages.generate)}
        </Button>
      </Box>

      <Box
        direction="column"
        gap={2}
        fullWidth
        style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        data-testid="composite-report-scroll-region"
      >
        {conflictNotice && (
          <Alert statusType="info">
            <AlertIcon icon={Info} />
            <AlertBody>{formatMessage(messages.alreadyGenerating)}</AlertBody>
          </Alert>
        )}

        {isBusy && !conflictNotice && (
          <Box gap={1} align="center">
            <DotLoader size={0} />
            <Text size="body">{formatMessage(messages.generating)}</Text>
          </Box>
        )}

        {error && (
          <Alert statusType="error">
            <AlertIcon icon={AlertCircle} />
            <AlertBody>{error.message}</AlertBody>
          </Alert>
        )}

        {isLoadingCarePlans || isLoadingSections ? (
          <Box direction="column" align="center" fullWidth>
            <DotLoader size={0} />
          </Box>
        ) : sectionsError ? (
          <Alert statusType="error">
            <AlertIcon icon={AlertCircle} />
            <AlertBody>{formatMessage(messages.sectionsLoadError)}</AlertBody>
          </Alert>
        ) : sections.length > 0 ? (
          <Box direction="column" gap={3} fullWidth>
            {sections.map(section => (
              <Box key={section.id} direction="column" gap={1} fullWidth>
                {section.truncated && (
                  <Alert statusType="warning" fullWidth>
                    <AlertIcon icon={AlertTriangle} />
                    <AlertBody>
                      <Text size="subbody">
                        {formatMessage(messages.sectionTruncated)}
                      </Text>
                    </AlertBody>
                  </Alert>
                )}
                <ImpressionEditor.Component
                  patient={patient}
                  impression={section}
                  viewMode="expanded"
                  renderKnowledgeBaseCitations={false}
                  analyticsSurface="composite_section"
                />
                <SectionRegenerateButton
                  templateId={section.templateId}
                  crdId={sectionsCrdId}
                  isHumanEdited={!section.aiGenerated}
                  invoke={async (id, opts) => {
                    await invoke(id, opts);
                  }}
                  isInvoking={isInvoking}
                  isPolling={isPolling}
                  error={error}
                  // Intentionally no `onRegenerate` here: `invoke()` resolving
                  // only means the request was accepted, not that generation
                  // finished (that happens later via polling). Refetching here
                  // would fire before updated content exists — including on a
                  // 409 conflict, where `invoke()` resolves without throwing.
                  // The shared `useCompositeReportGeneration` instance already
                  // calls `handleGenerationSuccess` (as `onGenerationSuccess`)
                  // once the task actually completes, covering regenerates too
                  // since they share the same per-subject task/polling loop.
                />
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyPageState
            illustration={FileText}
            message={formatMessage(messages.noReportYet)}
          />
        )}
      </Box>
    </Box>
  );
};

const CompositeReportModule: React.FC<CompositeReportModuleProps> = props => {
  const isEnabled = useIsCompositeReportEnabled();

  if (!isEnabled) {
    return null;
  }

  return <CompositeReportModuleContent {...props} />;
};

export default CompositeReportModule;
